import { CommonModule } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { MenuItem, MessageService } from 'primeng/api';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MenuModule } from 'primeng/menu';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import {
  BehaviorSubject,
  concatMap,
  finalize,
  NEVER,
  Observable,
  of,
  skipWhile,
  switchMap,
  take,
  timer,
} from 'rxjs';

import { AuthState, selectActiveAccount } from '../../../../core/auth/auth.reducer';
import { ColumnConfig } from '../../../../shared/components/table-header/header-config.model';
import { TableHeaderComponent } from '../../../../shared/components/table-header/table-header.component';
import { PaginationResponse } from '../../../../shared/models/pagination-response.model';
import { ServiceRequestListWidgetConfiguration } from '../../../widgets/models/service-request-list-widget.model';
import {
  WidgetComponent,
  WidgetConfigurationType,
} from '../../../widgets/models/widget-configuration.model';
import { WidgetVisibilityService } from '../../../widgets/widget-visibility.service';
import { ServiceRequest } from '../../service-request.model';
import { ServiceRequestService } from '../../service-request.service';
import { ServiceRequestListWidgetConfigurationComponent } from '../service-request-list-configuration/service-request-list-widget-configuration.component';

@Component({
  selector: 'carehub-user-service-request-list-widget',
  imports: [
    CommonModule,
    TableModule,
    BadgeModule,
    ButtonModule,
    MenuModule,
    PaginatorModule,
    RouterModule,
    ReactiveFormsModule,
    TableHeaderComponent,
    TagModule,
    TooltipModule,
    CardModule,
    InputTextModule,
    InputNumberModule,
    FormsModule,
    ServiceRequestListWidgetConfigurationComponent,
  ],
  templateUrl: './service-request-list-widget.component.html',
  styleUrl: './service-request-list-widget.component.scss',
  providers: [WidgetVisibilityService],
})
export class ServiceRequestListWidgetComponent
  implements AfterViewInit, OnInit, OnDestroy, WidgetComponent {
  @ViewChild('responsiveWidgetTable') tableCard!: ElementRef<HTMLDivElement>;
  @ViewChild('configurationComponent')
  configurationComponent!: ServiceRequestListWidgetConfigurationComponent;

  @Input() isEditing: boolean = false;
  @Input() configuration: ServiceRequestListWidgetConfiguration | undefined;

  @Input() configurationChange: EventEmitter<WidgetConfigurationType> =
    new EventEmitter<WidgetConfigurationType>();

  @Input() configurationDelete: EventEmitter<WidgetConfigurationType> =
    new EventEmitter<WidgetConfigurationType>();

  @Input() loadingChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  actionMenuItems: MenuItem[] = [
    {
      label: $localize`:@@common.button.edit:Edit`,
      command: () => this.onEdit(),
    },
    {
      label: $localize`:@@common.button.delete:Delete`,
      command: () => this.onDelete(),
    },
  ];

  serviceRequests$: Observable<PaginationResponse<ServiceRequest[]>> = of({
    data: [],
    totalCount: 0,
    pageNumber: 1,
    pageSize: 10,
  });

  headerConfig: ColumnConfig[] = [
    {
      header: $localize`:@@sr_list_widget.table.header.title:Title`,
      sortable: false,
      priority: 1,
      minWidth: 140,
    },
    {
      header: $localize`:@@sr_list_widget.table.header.status:Status`,
      sortable: false,
      priority: 1,
      minWidth: 30,
    },
    {
      header: $localize`:@@sr_list_widget.table.header.assigned_to:Assigned to`,
      sortable: false,
      priority: 3,
      minWidth: 100,
    },
    {
      header: $localize`:@@sr_list_widget.table.header.priority:Priority`,
      sortable: false,
      priority: 2,
      minWidth: 30,
    },
    {
      header: $localize`:@@sr_list_widget.table.header.created_date:Created date`,
      sortable: false,
      priority: 4,
      minWidth: 100,
    },
    { header: '', sortable: false },
  ];

  isPaused$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private _router: Router,
    private _messageService: MessageService,
    private _serviceRequestService: ServiceRequestService,
    private _widgetVisibilityService: WidgetVisibilityService,
    private _store: Store<AuthState>,
    private _changeDetector: ChangeDetectorRef
  ) {}

  ngAfterViewInit(): void {
    this._widgetVisibilityService.setupResizeObserver(this.tableCard.nativeElement);
  }

  ngOnInit(): void {
    this.isPaused$.next(this.isEditing);

    const reloadMilliseconds: number = (this.configuration?.reloadDelay ?? 5) * 1000 * 60;

    this.isPaused$
      .pipe(switchMap(isPaused => (isPaused ? NEVER : timer(0, reloadMilliseconds))))
      .subscribe(() => {
        this.populateUserRequests(undefined, this.configuration?.searchTerm);
      });
  }

  ngOnDestroy(): void {
    this._widgetVisibilityService.disconnect();
  }

  get serviceRequestConfig(): ServiceRequestListWidgetConfiguration | undefined {
    return this.configuration as ServiceRequestListWidgetConfiguration;
  }

  onEdit(): void {
    this.isEditing = true;
    this.isPaused$.next(true);
    this._changeDetector.detectChanges();
  }

  onConfigurationUpdate(): void {
    if (this.configurationComponent && this.configurationComponent.isValid()) {
      const newConfiguration: ServiceRequestListWidgetConfiguration =
        this.configurationComponent.getConfiguration();

      this.isEditing = false;
      this.isPaused$.next(false);

      this.configurationChange.emit(newConfiguration);
    }
  }

  onConfigurationCancel(): void {
    this.isEditing = false;
    this.isPaused$.next(false);
  }

  isConfigurationValid(): boolean {
    return this.configurationComponent?.isValid() || false;
  }

  onDelete(): void {
    this.configurationDelete.emit(this.configuration);
  }

  formatStatusLabel(status: string): string {
    return status.toLowerCase().replace(/\s|\/+/g, '-');
  }

  startTeamsChat(serviceRequest: ServiceRequest): void {
    if (serviceRequest.assignedTo === 'Unassigned') {
      this._messageService.add({
        severity: 'warn',
        detail: $localize`:@@sr_list_widget.message.unassigned_ticket:The ticket has not been assigned to anyone yet, so you can not chat with someone about this ticket.`,
        life: 5000,
      });
      return;
    }
    this._serviceRequestService.joinTeamsChat(serviceRequest);
  }

  editServiceRequest(serviceRequest: ServiceRequest): void {
    this._router.navigate(['/utilities/service-requests/' + serviceRequest.id]);
  }

  onPageChange(paginatorState: PaginatorState): void {
    this.populateUserRequests(paginatorState, this.configuration?.searchTerm);
  }

  isColumnVisible(priority: number): boolean {
    return this._widgetVisibilityService.isColumnVisible(priority);
  }

  getVisibleHeaderConfig(): ColumnConfig[] {
    return this._widgetVisibilityService.getVisibleColumns(this.headerConfig);
  }

  private populateUserRequests(paginator?: PaginatorState, searchTerm?: string | undefined): void {
    this.loadingChange?.emit(true);

    this.serviceRequests$ = this._store.select(selectActiveAccount).pipe(
      skipWhile(activeAccount => activeAccount === null || activeAccount === undefined),
      take(1),
      concatMap(async (activeAccount) => {
        let params: HttpParams = new HttpParams().set('Requester', activeAccount!.name);
        params = params.append('sort', 'Id:desc');

        if (searchTerm) {
          params = params.append('searchTerm', searchTerm);
        }

        const page: number = paginator?.page ? paginator.page + 1 : 1;
        const pageSize: number = paginator?.rows ?? 10;

        return await this._serviceRequestService.get(page, params, pageSize);
      }),
      finalize(() => {
        this.loadingChange?.emit(false);
      })
    );
  }
}
